import {
  api,
  compareSeq,
  maxSeq,
  type ConversationDetail,
  type ConversationEvent,
  type ConversationSummary,
  type FileInfo,
  type InstanceState,
  type Invitation,
  type Me,
  type Message,
  type Person,
  type Seq,
  type UserEvent,
} from '@sd/contracts';
import { ClientModule, errorCode, signOutReason, SIGN_OUT_CODES, type ClientContext } from './context';
import { applyEventToSummary, applyEventToTimeline, eventPosition, mergeSummary, upsertLoaded, upsertMessages } from './merge';
import type { Outbox } from './outbox';
import { newerMe, peopleIn, withAnswer, withProfile } from './people';
import { reconnectDelayMs, type RealtimeConnection, type RealtimeFactory } from './realtime';
import { isDndOn, type ClientState, type Timeline } from './state';
import type { Uploads } from './uploads';

/** Messages a conversation keeps when one opens another (its newest; older pages load again when scrolled to). */
const KEEP_ON_LEAVE = 100;
/** Conversations prefetched at once (sync.prefetch). */
const PREFETCH_AT_ONCE = 2;
const READ_DEBOUNCE_MS = 300;
const RECEIPT_REFRESH_MS = 400;
const MAINTENANCE_FIRST_CHECK_MS = 2_000;
const MAINTENANCE_CHECK_MS = 30_000;
/** How often an owner's or admin's app re-reads server info, so a stopped virus scanner shows without opening the admin screen. */
export const ADMIN_HEALTH_CHECK_MS = 5 * 60_000;
/** How long "입력 중" shows after the last word from someone typing; their app repeats it every TYPING_REPEAT_MS. */
export const TYPING_SHOW_MS = 6_000;
export const TYPING_REPEAT_MS = 3_000;

/**
 * Keeps the store in step with the server: the realtime socket, catch-ups
 * after gaps and reconnects, page loading and the viewer's read state.
 */
export class Sync extends ClientModule {
  private realtime: RealtimeConnection | null = null;
  private everConnected = false;
  private reconnectAttempt = 0;
  private sessionId: string | null = null;
  private readonly catchUps = new Map<string, Promise<void>>();
  private readonly catchUpAgain = new Set<string>();
  /** Last applied user event; null until the first baseline is known. */
  private userCursor: Seq | null = null;
  private userCatchUp: Promise<void> | null = null;
  private userCatchUpAgain = false;
  /** Newest message seq already notified per conversation. */
  private readonly notifiedSeq = new Map<string, Seq>();
  /** The message each conversation's OS notification shows (a newer one replaces it). */
  private readonly shownSeq = new Map<string, Seq>();
  /** Conversations the viewer wrote in from an older slice: they return to the newest messages once that page loads. */
  private readonly backToLatest = new Set<string>();
  private viewing: { conversationId: string; atBottom: boolean } | null = null;

  constructor(
    ctx: ClientContext,
    private readonly realtimeFactory: RealtimeFactory | null,
    private readonly outbox: Outbox,
    private readonly uploads: Uploads,
  ) {
    super(ctx);
  }

  /** People, then the user-event baseline and the conversation list. */
  async loadInitial(): Promise<void> {
    // The user-event baseline is taken before the list snapshot so nothing in between is missed.
    await Promise.all([this.loadPeople(), this.catchUpUserEvents().catch(() => {}).then(() => Promise.all([this.loadConversations(), this.loadSavedIds(), this.loadScheduled()]))]);
  }

  connectRealtime() {
    if (!this.realtimeFactory) {
      this.set({ connection: 'online' });
      void this.outbox.flushQueue();
      return;
    }
    this.realtime = this.realtimeFactory(this.ctx.platform.transport(this.ctx.serverId), {
      onConnect: () => {
        this.reconnectAttempt = 0;
        // The first connect catches up too: nothing was delivered between the list snapshot and now.
        const initial = !this.everConnected;
        this.everConnected = true;
        void this.resync({ initial });
      },
      onDisconnect: (_reason, retrying) => {
        if (this.stopped) return;
        // Nobody's "입력 중" can be kept up to date while offline.
        this.set({ connection: 'offline', typing: {} });
        if (!retrying) this.scheduleReconnect();
      },
      onConnectError: (_message, retrying) => {
        if (this.stopped) return;
        this.set({ connection: 'offline' });
        if (!retrying) void this.checkSessionThenReconnect();
      },
      onReady: (ready) => {
        this.sessionId = ready.sessionId;
        const online: Record<string, boolean> = {};
        for (const id of ready.onlineUserIds) online[id] = true;
        this.set({ online });
      },
      onConversationEvent: (event) => void this.handleConversationEvent(event),
      onUserEvent: (event) => this.receiveUserEvent(event),
      onPresence: (event) => this.set((s) => ({ online: { ...s.online, [event.userId]: event.online } })),
      onServerState: (event) => this.set((s) => (s.serverInfo ? { serverInfo: { ...s.serverInfo, state: event.state } } : {})),
      onTyping: (event) => this.showTyping(event.conversationId, event.userId),
    });
    this.realtime.connect();
  }

  disconnect() {
    this.realtime?.disconnect();
    this.realtime = null;
  }

  /** The network is back or the PC woke up: reconnect, or catch up if the socket stayed up. */
  resume() {
    if (this.realtime && !this.realtime.connected) this.reconnectNow();
    else void this.resync();
  }

  private scheduleReconnect() {
    if (this.stopped || this.timers.has('reconnect')) return;
    this.timers.after('reconnect', reconnectDelayMs(this.reconnectAttempt), () => {
      if (this.realtime && !this.realtime.connected) this.realtime.connect();
    });
    this.reconnectAttempt += 1;
  }

  private reconnectNow() {
    this.timers.clear('reconnect');
    this.reconnectAttempt = 0;
    this.realtime?.connect();
  }

  /** The server refused the handshake without saying why: only REST can tell an ended session from a passing failure. */
  private async checkSessionThenReconnect() {
    try {
      await this.api.call(api.users.me);
    } catch (error) {
      const code = errorCode(error);
      if (SIGN_OUT_CODES.has(code)) {
        this.ctx.signOut(signOutReason(code));
        return;
      }
    }
    this.scheduleReconnect();
  }

  /** Approved maintenance holds the outbox (writes answer 503) until the server is ACTIVE again. */
  serverStateChanged(before: InstanceState | undefined, after: InstanceState | undefined) {
    if (before === after) return;
    if (after === 'MAINTENANCE') {
      this.checkDuringMaintenance(MAINTENANCE_FIRST_CHECK_MS);
      return;
    }
    this.timers.clear('maintenance');
    if (before && after === 'ACTIVE') void this.outbox.flushQueue();
  }

  /**
   * Re-reads server info until maintenance is over, in case the socket misses
   * the announcement: soon at first, since a proxy's 503 reads as MAINTENANCE
   * too, then every 30 s.
   */
  private checkDuringMaintenance(delay: number) {
    this.timers.after('maintenance', delay, () => {
      void this.refreshServerInfo().catch(() => {});
      this.checkDuringMaintenance(MAINTENANCE_CHECK_MS);
    });
  }

  /** Someone else types in a conversation: "입력 중" until TYPING_SHOW_MS after the last word, or their message. */
  private showTyping(conversationId: string, userId: string) {
    if (userId === this.state.me?.id) return;
    const until = this.ctx.now().getTime() + TYPING_SHOW_MS;
    this.set((s) => ({ typing: { ...s.typing, [conversationId]: { ...s.typing[conversationId], [userId]: until } } }));
    this.timers.after(`typing:${conversationId}:${userId}`, TYPING_SHOW_MS, () => this.stopTyping(conversationId, userId));
  }

  private stopTyping(conversationId: string, userId: string) {
    this.timers.clear(`typing:${conversationId}:${userId}`);
    if (!this.state.typing[conversationId]?.[userId]) return;
    this.set((s) => {
      const { [userId]: _ended, ...others } = s.typing[conversationId] ?? {};
      return { typing: { ...s.typing, [conversationId]: others } };
    });
  }

  /** For owners and admins: server info (with the scanner's health) again every few minutes. */
  watchHealthForAdmins() {
    this.timers.every('admin-health', ADMIN_HEALTH_CHECK_MS, () => {
      const role = this.state.me?.role;
      if (role === 'owner' || role === 'admin') void this.refreshServerInfo().catch(() => {});
    });
  }

  /** Re-reads company name, limits and state; a changed `syncGeneration` means the database was restored. */
  async refreshServerInfo(): Promise<void> {
    const serverInfo = await this.api.call(api.meta.serverInfo);
    if (this.stopped) return;
    const before = this.state.serverInfo?.syncGeneration;
    if (JSON.stringify(this.state.serverInfo) !== JSON.stringify(serverInfo)) this.set({ serverInfo });
    if (before && serverInfo.syncGeneration && before !== serverInfo.syncGeneration) await this.reloadAfterRestore();
  }

  /**
   * Cached timelines may be ahead of a restored database: drop them and load fresh snapshots. Pending sends stay queued.
   * People are taken as the restored directory has them, even where a copy here showed a later change.
   */
  private async reloadAfterRestore(): Promise<void> {
    const active = this.state.activeConversationId;
    this.set({ timelines: {}, details: {}, conversationFiles: {} });
    await Promise.all([this.loadPeople({ authoritative: true }), this.loadConversations()]);
    if (active && this.state.conversations[active]) await this.openConversation(active);
  }

  /**
   * After a reconnect: catch up user events, the list and open timelines,
   * resume scans and send the outbox. Each part runs even when another fails,
   * and the outbox does not wait for any of them. `initial` (the first
   * connect) shows neither the sync banner nor its notice.
   */
  async resync(options: { initial?: boolean } = {}): Promise<void> {
    if (this.stopped) return;
    this.set({ connection: options.initial ? 'online' : 'syncing' });
    const flushing = this.outbox.flushQueue();
    // The first connect follows the start, which has just read it: read again alongside the catching up rather than
    // before it (one round trip less), still in time to see a server updated in between.
    if (options.initial) void this.refreshServerInfo().catch(() => {});
    else await this.refreshServerInfo().catch(() => {});
    const failures: unknown[] = [];
    const attempt = (work: Promise<void>) =>
      work.then(
        () => true,
        (error: unknown) => {
          failures.push(error);
          return false;
        },
      );
    await attempt(this.catchUpUserEvents());
    if (this.stopped) return;
    const listed = await attempt(this.loadConversations());
    await Promise.all(Object.keys(this.state.timelines).map((id) => attempt(this.catchUp(id))));
    const ended = failures.map(errorCode).find((code) => SIGN_OUT_CODES.has(code));
    if (ended) this.ctx.signOut(signOutReason(ended));
    if (this.stopped) return;
    for (const id of this.backToLatest) void this.showLatest(id).catch(() => {});
    this.uploads.resumeScans();
    const flushed = await flushing;
    if (this.stopped) return;
    this.set({ connection: !this.realtime || this.realtime.connected ? 'online' : 'offline' });
    if (listed && !options.initial) this.ctx.notice({ kind: 'sync.done', flushed });
  }

  /** At the bottom of the newest messages: an older slice (search jump) is not, whatever its scroll position. */
  private isViewing(conversationId: string): boolean {
    return this.viewing?.conversationId === conversationId && this.viewing.atBottom && this.ctx.isFocused() && !this.state.timelines[conversationId]?.hasMoreAfter;
  }

  private async handleConversationEvent(event: ConversationEvent) {
    const id = event.conversationId;
    const summary = this.state.conversations[id];
    if (!summary) {
      await this.loadDetail(id).catch(() => {});
      return;
    }
    const timeline = this.state.timelines[id];
    const position = timeline?.loaded ? eventPosition(timeline.eventCursor, event) : 'applied';
    if (position === 'gap') {
      // A failed catch-up is retried by the next event or resync.
      await this.catchUp(id).catch(() => {});
    } else if (position === 'next') {
      this.set((s) => {
        const t = s.timelines[id];
        return t ? { timelines: { ...s.timelines, [id]: applyEventToTimeline(t, event) } } : {};
      });
    }
    const meId = this.state.me?.id ?? '';
    this.set((s) => {
      const current = s.conversations[id];
      if (!current) return {};
      return { conversations: { ...s.conversations, [id]: applyEventToSummary(current, event, { meId, viewing: this.isViewing(id) }) } };
    });

    const data = event.data;
    if (data.type === 'message.created') {
      const message = data.message;
      if (message.senderId) this.stopTyping(id, message.senderId);
      if (message.clientMessageId && this.state.pending[message.clientMessageId]) this.outbox.removePending(message.clientMessageId);
      if (message.senderId !== meId && message.kind === 'text') {
        this.maybeNotify(summary, message);
        if (this.isViewing(id)) this.scheduleMarkRead(id);
      }
    } else if (data.type === 'message.deleted') {
      this.withdrawIfShown(data.message);
    } else if (data.type === 'read.updated' && data.userId !== meId) {
      this.scheduleReceiptRefresh(id);
    } else if (data.type.startsWith('member.') || data.type === 'conversation.updated') {
      if (this.state.details[id]) void this.loadDetail(id).catch(() => {});
    }
    this.ctx.updateBadge();
  }

  /** The ids of the viewer's saved messages, where the server keeps them. Best effort: none is no failure. */
  async loadSavedIds(): Promise<void> {
    if (!this.state.serverInfo?.capabilities.includes('messages.saved')) return;
    const res = await this.api.call(api.messages.savedIds).catch(() => null);
    if (res && !this.stopped) this.set({ saved: Object.fromEntries(res.messageIds.map((id) => [id, true as const])) });
  }

  /** The viewer's scheduled messages still waiting, where the server sends them. Best effort like the saved ids. */
  async loadScheduled(): Promise<void> {
    if (!this.state.serverInfo?.capabilities.includes('messages.scheduled')) return;
    const res = await this.api.call(api.messages.scheduled).catch(() => null);
    if (res && !this.stopped) this.set({ scheduled: Object.fromEntries(res.scheduled.map((s) => [s.id, s])) });
  }

  private async handleUserEvent(event: UserEvent) {
    const data = event.data;
    switch (data.type) {
      case 'scheduled.updated': {
        const item = data.scheduled;
        this.set((s) => {
          const { [item.id]: _was, ...others } = s.scheduled;
          return { scheduled: item.status === 'scheduled' ? { ...others, [item.id]: item } : others };
        });
        if (item.status === 'failed') this.ctx.notice({ kind: 'scheduled.failed', conversationId: item.conversationId, text: item.text, code: item.error ?? 'INTERNAL' });
        break;
      }
      case 'message.saved':
        this.set((s) => {
          const { [data.messageId]: _was, ...others } = s.saved;
          return { saved: data.saved ? { ...others, [data.messageId]: true } : others };
        });
        break;
      case 'conversation.added':
        await this.loadDetail(data.conversationId).catch(() => {});
        break;
      case 'conversation.removed':
        this.set((s) => dropConversations(s, (id) => id === data.conversationId));
        break;
      case 'membership.updated':
        this.set((s) => {
          const c = s.conversations[data.conversationId];
          if (!c) return {};
          const lastReadSeq = maxSeq(c.my.lastReadSeq, data.lastReadSeq);
          const read = compareSeq(lastReadSeq, c.lastSeq) >= 0;
          return {
            conversations: {
              ...s.conversations,
              [c.id]: { ...c, my: { ...c.my, muted: data.muted, pinned: data.pinned, lastReadSeq }, unreadCount: read ? 0 : c.unreadCount, mentioned: read ? false : c.mentioned },
            },
          };
        });
        break;
      case 'upload.updated':
        this.uploads.applyScannedFile(data.file);
        break;
      case 'invitations.changed':
        if (this.state.invitations.length) void this.loadInvitations().catch(() => {});
        break;
      case 'session.revoked':
        if (this.sessionId && data.sessionId === this.sessionId) this.ctx.signOut('revoked');
        break;
      case 'account.deactivated':
        this.ctx.signOut('deactivated');
        break;
      case 'user.updated': {
        const { type: _type, ...update } = data;
        this.set((s) => withProfile(s, update));
        // Screens with their own copy of people (the admin member list) read it again.
        this.ctx.notice({ kind: 'profile.updated', userId: data.userId });
        break;
      }
      default:
        break;
    }
    this.ctx.updateBadge();
  }

  /** Applies a socket user event in cursor order; a gap is filled from REST first. */
  private receiveUserEvent(event: UserEvent) {
    const cursor = this.userCursor;
    if (cursor !== null && compareSeq(event.seq, cursor) <= 0) return;
    if (cursor !== null && BigInt(event.seq) !== BigInt(cursor) + 1n) {
      void this.catchUpUserEvents().catch(() => {});
      return;
    }
    this.userCursor = event.seq;
    void this.handleUserEvent(event);
  }

  /**
   * Applies user events after the cursor: removed from a group, read on
   * another device, session revoked. Without a cursor it only takes the
   * current one as the baseline. Serialised like conversation catch-ups.
   */
  private catchUpUserEvents(): Promise<void> {
    if (this.userCatchUp) {
      this.userCatchUpAgain = true;
      return this.userCatchUp;
    }
    const run = (async () => {
      do {
        this.userCatchUpAgain = false;
        for (let page = 0; page < 50 && !this.stopped; page += 1) {
          const after = this.userCursor;
          const res = await this.api.call(api.events.user, { query: { after: after ?? '0', limit: after === null ? 1 : 200 } });
          if (after === null || res.resetRequired) {
            this.userCursor = res.currentSeq;
            // Past the replay window: the conversation list and the directory (photos) are the snapshots to continue from.
            if (after !== null) await Promise.all([this.loadConversations(), this.loadPeople(), this.loadSavedIds(), this.loadScheduled()]);
            break;
          }
          for (const event of res.events) {
            if (this.stopped) return;
            const cursor = this.userCursor;
            if (cursor !== null && compareSeq(event.seq, cursor) <= 0) continue;
            this.userCursor = event.seq;
            await this.handleUserEvent(event);
          }
          this.userCursor = maxSeq(this.userCursor ?? '0', res.nextAfter);
          if (!res.hasMore) break;
        }
      } while (this.userCatchUpAgain && !this.stopped);
    })().finally(() => {
      this.userCatchUp = null;
    });
    this.userCatchUp = run;
    return run;
  }

  /** Fetches events after the timeline cursor until caught up. Serialised per conversation. */
  async catchUp(conversationId: string): Promise<void> {
    const running = this.catchUps.get(conversationId);
    if (running) {
      this.catchUpAgain.add(conversationId);
      return running;
    }
    const run = (async () => {
      do {
        this.catchUpAgain.delete(conversationId);
        for (let page = 0; page < 50; page += 1) {
          const timeline = this.state.timelines[conversationId];
          if (!timeline?.loaded) return;
          const res = await this.api.call(api.events.conversation, { params: { conversationId }, query: { after: timeline.eventCursor, limit: 200 } });
          if (res.resetRequired) {
            await this.loadLatest(conversationId, res.currentEventSeq);
            break;
          }
          const meId = this.state.me?.id ?? '';
          this.set((s) => {
            let t = s.timelines[conversationId];
            let c = s.conversations[conversationId];
            if (!t) return {};
            // Nothing new (most reconnects): the timeline stays the same object and nothing is drawn again.
            if (!res.events.length && maxSeq(t.eventCursor, res.nextAfter) === t.eventCursor) return {};
            for (const event of res.events) {
              t = applyEventToTimeline(t, event);
              if (c) c = applyEventToSummary(c, event, { meId, viewing: this.isViewing(conversationId) });
            }
            t = { ...t, eventCursor: maxSeq(t.eventCursor, res.nextAfter) };
            return { timelines: { ...s.timelines, [conversationId]: t }, ...(c ? { conversations: { ...s.conversations, [conversationId]: c } } : {}) };
          });
          for (const event of res.events) {
            if (event.data.type === 'message.created' && event.data.message.clientMessageId) {
              const cmid = event.data.message.clientMessageId;
              if (this.state.pending[cmid]) this.outbox.removePending(cmid);
            }
            if (event.data.type === 'message.deleted') this.withdrawIfShown(event.data.message);
          }
          if (!res.hasMore) break;
        }
      } while (this.catchUpAgain.has(conversationId));
    })()
      .catch((error: unknown) => {
        // Removed from the conversation meanwhile: the server no longer admits it exists.
        if (errorCode(error) !== 'NOT_FOUND') throw error;
        this.set((s) => dropConversations(s, (id) => id === conversationId));
      })
      .finally(() => this.catchUps.delete(conversationId));
    this.catchUps.set(conversationId, run);
    return run;
  }

  /** Re-reads the signed-in profile (e.g. after an ownership transfer changed the role). */
  async refreshMe(): Promise<void> {
    this.acceptMe(await this.api.call(api.users.me));
  }

  /** The profile as an answer has it, in every copy the store keeps, unless a copy shows a later change. */
  acceptMe(me: Me) {
    this.set((s) => withAnswer(s, { me: newerMe(s.me, me) }, [me]));
  }

  /** Stores an answer's copy of a conversation; the people in it end at the newest copy known. */
  acceptSummary(summary: ConversationSummary, merge: (current: ConversationSummary | undefined) => ConversationSummary) {
    this.set((s) => withAnswer(s, { conversations: { ...s.conversations, [summary.id]: merge(s.conversations[summary.id]) } }, peopleIn(summary)));
  }

  /** The directory. `authoritative`: after a restore, where it is the truth even against copies that look newer. */
  async loadPeople(options: { authoritative?: boolean } = {}): Promise<void> {
    const people: Record<string, Person> = {};
    let cursor: string | undefined;
    for (let page = 0; page < 20; page += 1) {
      const res = await this.api.call(api.users.list, { query: { includeDeactivated: 'true', limit: 200, ...(cursor ? { cursor } : {}) } });
      for (const person of res.users) people[person.id] = person;
      if (!res.nextCursor) break;
      cursor = res.nextCursor;
    }
    const online: Record<string, boolean> = { ...this.state.online };
    for (const person of Object.values(people)) if (online[person.id] === undefined) online[person.id] = person.online;
    this.set((s) => withAnswer(s, { people, online }, Object.values(people), options.authoritative));
  }

  async loadConversations(): Promise<void> {
    const res = await this.api.call(api.conversations.list);
    const people = res.conversations.flatMap(peopleIn);
    this.set((s) => {
      const conversations: Record<string, ConversationSummary> = {};
      for (const c of res.conversations) conversations[c.id] = mergeSummary(s.conversations[c.id], c);
      // Every summary as it was (most reconnects): the list keeps its identity and nothing that shows it redraws.
      const ids = Object.keys(conversations);
      const same = ids.length === Object.keys(s.conversations).length && ids.every((id) => conversations[id] === s.conversations[id]);
      return withAnswer(s, same ? {} : { ...dropConversations(s, (id) => !conversations[id]), conversations }, people);
    });
    this.rememberPeople(people);
    this.ctx.updateBadge();
  }

  async loadInvitations(): Promise<Invitation[]> {
    const res = await this.api.call(api.invitations.list);
    this.set({ invitations: res.invitations });
    return res.invitations;
  }

  private rememberPeople(people: readonly Person[]) {
    if (!people.length) return;
    this.set((s) => {
      let changed = false;
      const next = { ...s.people };
      for (const person of people) {
        if (!next[person.id]) {
          next[person.id] = person;
          changed = true;
        }
      }
      return changed ? { people: next } : {};
    });
  }

  storeDetail(detail: ConversationDetail) {
    const { members: _members, createdBy: _createdBy, ...summary } = detail;
    this.set((s) =>
      withAnswer(
        s,
        { conversations: { ...s.conversations, [detail.id]: mergeSummary(s.conversations[detail.id], summary) }, details: { ...s.details, [detail.id]: detail } },
        peopleIn(detail),
      ),
    );
    this.rememberPeople(detail.members.map((m) => m.person));
  }

  async loadDetail(conversationId: string): Promise<ConversationDetail> {
    const { conversation } = await this.api.call(api.conversations.get, { params: { conversationId } });
    this.storeDetail(conversation);
    return conversation;
  }

  async loadConversationFiles(conversationId: string): Promise<FileInfo[]> {
    const res = await this.api.call(api.files.listForConversation, { params: { conversationId }, query: { limit: 30 } });
    this.set((s) => ({ conversationFiles: { ...s.conversationFiles, [conversationId]: res.files } }));
    return res.files;
  }

  /** Loads the newest page; the event cursor is captured first so nothing between is lost. */
  private async loadLatest(conversationId: string, cursorOverride?: Seq, around?: string): Promise<void> {
    const summary = this.state.conversations[conversationId];
    const cursor = cursorOverride ?? summary?.lastEventSeq ?? '0';
    this.set((s) => ({
      timelines: {
        ...s.timelines,
        [conversationId]: {
          ...(s.timelines[conversationId] ?? emptyTimeline(conversationId)),
          loading: true,
        },
      },
    }));
    const res = await this.api.call(api.messages.list, { params: { conversationId }, query: around ? { around, limit: 50 } : { limit: 50 } }).catch((error: unknown) => {
      this.stopLoading(conversationId);
      throw error;
    });
    const anchor = unreadAnchor(summary, res.messages, this.state.me?.id ?? '');
    this.set((s) => ({
      timelines: {
        ...s.timelines,
        [conversationId]: {
          conversationId,
          messages: res.messages,
          hasMoreBefore: res.hasMoreBefore,
          hasMoreAfter: res.hasMoreAfter,
          historyStartSeq: res.historyStartSeq,
          eventCursor: cursor,
          loading: false,
          loaded: true,
          unreadAnchorSeq: s.timelines[conversationId]?.loaded ? (s.timelines[conversationId]?.unreadAnchorSeq ?? anchor) : anchor,
        },
      },
    }));
    // A send stored just before a crash, whose answer was lost, is on this page: it must not also show as waiting.
    for (const m of res.messages) if (m.clientMessageId && this.state.pending[m.clientMessageId]) this.outbox.removePending(m.clientMessageId);
  }

  /** A page request failed: the timeline no longer shows it loading. */
  private stopLoading(conversationId: string) {
    this.set((s) => {
      const t = s.timelines[conversationId];
      return t ? { timelines: { ...s.timelines, [conversationId]: { ...t, loading: false } } } : {};
    });
  }

  /** Opens a conversation (loads its newest page or the page around `messageId`). */
  /** Conversations whose newest page is loading ahead of being opened. */
  private readonly prefetching = new Map<string, Promise<void>>();

  /**
   * Loads the newest page of conversations not opened yet, two at a time, so that opening one shows its messages at
   * once instead of "메시지를 불러오는 중…". Loaded, they stay current with the realtime events like any other.
   */
  async prefetch(conversationIds: readonly string[]): Promise<void> {
    const wanted = (id: string) => this.state.conversations[id] && !this.state.timelines[id]?.loaded && !this.state.timelines[id]?.loading && !this.prefetching.has(id);
    const queue = [...new Set(conversationIds)].filter(wanted);
    const one = async (id: string) => {
      if (!wanted(id)) return;
      const run = this.loadLatest(id)
        .then(() => this.catchUp(id))
        .finally(() => this.prefetching.delete(id));
      this.prefetching.set(id, run);
      await run.catch(() => {});
    };
    await Promise.all(
      Array.from({ length: Math.min(PREFETCH_AT_ONCE, queue.length) }, async () => {
        for (let id = queue.shift(); id && !this.stopped; id = queue.shift()) await one(id);
      }),
    );
  }

  async openConversation(conversationId: string, options: { messageId?: string } = {}): Promise<void> {
    const left = this.state.activeConversationId;
    this.set((s) => {
      const patch = { activeConversationId: conversationId, flash: null };
      const t = left && left !== conversationId ? s.timelines[left] : undefined;
      // The conversation left keeps only its newest messages (it opens at the newest again; older pages load on the
      // way up): each message kept was laid out again at every visit, 0.2 s for 500 on a phone.
      if (!t?.loaded || t.loading || t.hasMoreAfter || t.messages.length <= KEEP_ON_LEAVE) return patch;
      return { ...patch, timelines: { ...s.timelines, [left!]: { ...t, messages: t.messages.slice(-KEEP_ON_LEAVE), hasMoreBefore: true } } };
    });
    if (options.messageId) this.backToLatest.delete(conversationId);
    // Already on its way (prefetched, a press on its row): shown at once, with that page rather than a second request.
    await this.prefetching.get(conversationId)?.catch(() => {});
    const timeline = this.state.timelines[conversationId];
    const needsAround = options.messageId && !timeline?.messages.some((m) => m.id === options.messageId);
    // Opened normally, a slice left from a search jump goes back to the newest messages.
    const needsLatest = !options.messageId && timeline?.hasMoreAfter;
    try {
      if (!timeline?.loaded || needsAround || needsLatest) {
        await this.loadLatest(conversationId, undefined, needsAround ? options.messageId : undefined);
        await this.catchUp(conversationId);
      } else {
        // Re-opening: refresh the unread anchor for this visit.
        const anchor = unreadAnchor(this.state.conversations[conversationId], timeline.messages, this.state.me?.id ?? '');
        this.set((s) => ({ timelines: { ...s.timelines, [conversationId]: { ...timeline, unreadAnchorSeq: anchor } } }));
      }
      if (options.messageId) this.set({ flash: { messageId: options.messageId, nonce: Date.now() } });
      void this.loadDetail(conversationId).catch(() => {});
    } catch (error) {
      this.ctx.notice({ kind: 'error', code: errorCode(error), context: 'open' });
    }
  }

  async loadOlder(conversationId: string): Promise<void> {
    const timeline = this.state.timelines[conversationId];
    if (!timeline?.loaded || !timeline.hasMoreBefore || timeline.loading) return;
    const first = timeline.messages[0];
    if (!first) return;
    this.set((s) => ({ timelines: { ...s.timelines, [conversationId]: { ...timeline, loading: true } } }));
    try {
      const res = await this.api.call(api.messages.list, { params: { conversationId }, query: { before: first.seq, limit: 50 } });
      this.set((s) => {
        const t = s.timelines[conversationId];
        // Replaced meanwhile ("최근 메시지로", another jump): this page no longer precedes it.
        if (!t || t.messages[0]?.seq !== first.seq) return {};
        return { timelines: { ...s.timelines, [conversationId]: { ...t, messages: upsertMessages(t.messages, res.messages), hasMoreBefore: res.hasMoreBefore, loading: false } } };
      });
    } catch (error) {
      this.stopLoading(conversationId);
      this.ctx.notice({ kind: 'error', code: errorCode(error), context: 'loadOlder' });
    }
  }

  /**
   * Next page after a slice opened around an older message. Reaching the
   * newest message, it catches up from the cursor taken before the request:
   * messages created meanwhile were left out of the slice.
   */
  async loadNewer(conversationId: string): Promise<void> {
    const timeline = this.state.timelines[conversationId];
    if (!timeline?.loaded || !timeline.hasMoreAfter || timeline.loading) return;
    const last = timeline.messages[timeline.messages.length - 1];
    if (!last) return;
    const cursor = timeline.eventCursor;
    this.set((s) => ({ timelines: { ...s.timelines, [conversationId]: { ...timeline, loading: true } } }));
    try {
      const res = await this.api.call(api.messages.list, { params: { conversationId }, query: { after: last.seq, limit: 50 } });
      let continued = false;
      this.set((s) => {
        const t = s.timelines[conversationId];
        // Replaced meanwhile ("최근 메시지로", another jump): this page no longer follows it.
        if (!t?.hasMoreAfter || t.messages[t.messages.length - 1]?.seq !== last.seq) return {};
        continued = true;
        const caughtUp = res.hasMoreAfter ? {} : { eventCursor: cursor };
        return { timelines: { ...s.timelines, [conversationId]: { ...t, ...caughtUp, messages: upsertMessages(t.messages, res.messages), hasMoreAfter: res.hasMoreAfter, loading: false } } };
      });
      if (continued && !res.hasMoreAfter) await this.catchUp(conversationId);
    } catch (error) {
      this.stopLoading(conversationId);
      this.ctx.notice({ kind: 'error', code: errorCode(error), context: 'loadNewer' });
    }
  }

  /** "최근 메시지로" from a slice opened around an older message: the newest page instead. */
  async showLatest(conversationId: string): Promise<void> {
    if (this.state.timelines[conversationId]?.hasMoreAfter) {
      await this.loadLatest(conversationId);
      await this.catchUp(conversationId);
    }
    this.backToLatest.delete(conversationId);
  }

  /** The viewer wrote from an older slice (search jump): back to the newest messages, now or after the next resync. */
  returnToLatest(conversationId: string) {
    if (!this.state.timelines[conversationId]?.hasMoreAfter) return;
    this.backToLatest.add(conversationId);
    void this.showLatest(conversationId).catch(() => {});
  }

  /** Called by the UI when the bottom of a conversation is (not) visible. */
  setViewing(conversationId: string | null, atBottom: boolean) {
    this.viewing = conversationId ? { conversationId, atBottom } : null;
    if (conversationId && atBottom) this.scheduleMarkRead(conversationId);
  }

  private scheduleMarkRead(conversationId: string) {
    this.timers.after(`read:${conversationId}`, READ_DEBOUNCE_MS, () => void this.markRead(conversationId));
  }

  /** Advances the read pointer to the newest loaded message (never backwards). */
  async markRead(conversationId: string): Promise<void> {
    if (!this.isViewing(conversationId)) return;
    const summary = this.state.conversations[conversationId];
    const timeline = this.state.timelines[conversationId];
    const last = timeline?.messages[timeline.messages.length - 1];
    if (!summary || !last || summary.readOnly && summary.my.state !== 'active' && summary.unreadCount === 0) return;
    if (compareSeq(last.seq, summary.my.lastReadSeq) <= 0 && summary.unreadCount === 0) return;
    try {
      const res = await this.api.call(api.conversations.markRead, { params: { conversationId }, body: { seq: last.seq } });
      this.set((s) => {
        const c = s.conversations[conversationId];
        if (!c) return {};
        return {
          conversations: {
            ...s.conversations,
            [conversationId]: { ...c, unreadCount: res.unreadCount, mentioned: res.unreadCount ? c.mentioned : false, my: { ...c.my, lastReadSeq: maxSeq(c.my.lastReadSeq, res.lastReadSeq) } },
          },
        };
      });
      this.ctx.updateBadge();
    } catch {
      // Retried on the next scroll/visibility change; nothing to show.
    }
  }

  private scheduleReceiptRefresh(conversationId: string) {
    if (!this.state.timelines[conversationId]?.loaded) return;
    this.timers.after(`receipts:${conversationId}`, RECEIPT_REFRESH_MS, () => void this.refreshReceipts(conversationId));
  }

  /** Re-reads the newest page to refresh "읽음 N" on the viewer's messages. */
  private async refreshReceipts(conversationId: string) {
    try {
      const res = await this.api.call(api.messages.list, { params: { conversationId }, query: { limit: 50 } });
      this.set((s) => {
        const t = s.timelines[conversationId];
        // The newest page is not next to an older slice: only refresh what it shows.
        return t ? { timelines: { ...s.timelines, [conversationId]: { ...t, messages: t.hasMoreAfter ? upsertLoaded(t.messages, res.messages) : upsertMessages(t.messages, res.messages) } } } : {};
      });
    } catch {
      // receipts refresh is best-effort
    }
  }

  private maybeNotify(summary: ConversationSummary, message: Message) {
    // A message delivered twice (a re-published event, a replay) notifies once.
    const notified = this.notifiedSeq.get(summary.id);
    if (notified !== undefined && compareSeq(message.seq, notified) <= 0) return;
    this.notifiedSeq.set(summary.id, message.seq);
    if (summary.my.muted || isDndOn(this.state, this.ctx.now())) return;
    if (this.state.activeConversationId === summary.id && this.ctx.isFocused()) return;
    const sender = (message.senderId ? this.state.people[message.senderId]?.displayName : undefined) ?? '';
    const title = summary.kind === 'group' ? `${summary.title ?? ''}` : sender;
    const body = `${summary.kind === 'group' && sender ? `${sender}: ` : ''}${message.text?.trim() ? message.text.slice(0, 200) : message.attachments[0] ? `[파일] ${message.attachments[0].name}` : ''}`;
    this.shownSeq.set(summary.id, message.seq);
    void this.ctx.platform.notify({ serverId: this.ctx.serverId, conversationId: summary.id, title, body, sender, messageId: message.id }).catch(() => {});
  }

  /** A deleted message must not stay readable in the notification that shows it. */
  private withdrawIfShown(message: Message) {
    if (this.shownSeq.get(message.conversationId) !== message.seq) return;
    this.shownSeq.delete(message.conversationId);
    void this.ctx.platform.withdrawNotifications?.(this.ctx.serverId, message.conversationId).catch(() => {});
  }
}

/** Forgets the cached state of conversations the viewer can no longer see. */
function dropConversations(s: ClientState, gone: (id: string) => boolean): Partial<ClientState> {
  const keep = <T>(map: Record<string, T>): Record<string, T> => (Object.keys(map).some(gone) ? Object.fromEntries(Object.entries(map).filter(([id]) => !gone(id))) : map);
  return {
    conversations: keep(s.conversations),
    timelines: keep(s.timelines),
    details: keep(s.details),
    conversationFiles: keep(s.conversationFiles),
    activeConversationId: s.activeConversationId && gone(s.activeConversationId) ? null : s.activeConversationId,
  };
}

/** The first unread message from someone else ("여기서부터 안 읽은 메시지"), while the conversation has unread ones. */
function unreadAnchor(summary: ConversationSummary | undefined, messages: readonly Message[], meId: string): Seq | null {
  return summary && summary.unreadCount > 0
    ? (messages.find((m) => m.kind === 'text' && m.senderId !== meId && compareSeq(m.seq, summary.my.lastReadSeq) > 0)?.seq ?? null)
    : null;
}

function emptyTimeline(conversationId: string): Timeline {
  return { conversationId, messages: [], hasMoreBefore: false, hasMoreAfter: false, historyStartSeq: '0', eventCursor: '0', loading: false, loaded: false, unreadAnchorSeq: null };
}
